// ** React Imports
import { type ReactNode } from 'react';

// ** MUI Imports
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// ** Layout Imports
// !Do not remove this Layout import
import Layout from '@erikdakoda/site-template-ui/layouts/Layout';

// ** Navigation Imports
import { VerticalNavItems } from '../config/navigation';
import { HorizontalNavItems } from '../config/navigation';
import { VerticalLandingNavItems } from '../config/navigation-landing';
import { HorizontalLandingNavItems } from '../config/navigation-landing';
import UviloBranding from '@/components/UviloBranding';
import Navigation from '@erikdakoda/site-template-ui/layouts/components/horizontal/navigation';

// ** Component Import
// Uncomment the below line (according to the layout type) when using server-side menu
// import ServerSideVerticalNavItems from './components/vertical/ServerSideNavItems'
// import ServerSideHorizontalNavItems from './components/horizontal/ServerSideNavItems'

import VerticalAppBarContent from './VerticalAppBarContent';
import HorizontalAppBarContent from './HorizontalAppBarContent';
import type { ContentAlign } from '@erikdakoda/site-template-ui/types';
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';

// ** Hook Import
import { useLayoutSettings } from '@erikdakoda/site-template-ui/hooks/useLayoutSettings';

interface Props {
  children: ReactNode;
  contentHeightFixed?: boolean;
  noPadding?: boolean;
  contentAlign?: ContentAlign;
}

const UserLayout = ({ children, contentHeightFixed, noPadding, contentAlign }: Props) => {
  // ** Hooks
  const { isAuthenticated } = useCurrentUser();
  const theme = useTheme();
  const { settings, saveSettings } = useLayoutSettings();
  const { navHidden } = settings;

  // ** Vars for server side navigation
  // const { menuItems: verticalMenuItems } = ServerSideVerticalNavItems()
  // const { menuItems: horizontalMenuItems } = ServerSideHorizontalNavItems()

  /**
   *  The below variable will hide the current layout menu at given screen size.
   *  The menu will be accessible from the Hamburger icon only (Vertical Overlay Menu).
   *  You can change the screen size from which you want to hide the current layout menu.
   *  Please refer useMediaQuery() hook: https://mui.com/material-ui/react-use-media-query/,
   *  to know more about what values can be passed to this hook.
   *  ! Do not change this value unless you know what you are doing. It can break the template.
   */
  const hidden = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  return (
    <Layout
      hidden={hidden}
      settings={settings}
      saveSettings={saveSettings}
      contentHeightFixed={contentHeightFixed}
      noPadding={noPadding}
      contentAlign={contentAlign}
      verticalLayoutProps={{
        navMenu: {
          navItems: isAuthenticated ? VerticalNavItems() : VerticalLandingNavItems(),
          branding: () => (
            <UviloBranding logoClass="solid" placement="center" sx={{ mt: 6, mb: 6 }} />
          ),
          // Uncomment the below line when using server-side menu in vertical layout and comment the above line
          // navItems: verticalMenuItems
        },
        appBar: {
          content: (props) => (
            <VerticalAppBarContent
              hidden={hidden}
              settings={settings}
              saveSettings={saveSettings}
              // @ts-expect-error
              toggleNavVisibility={props?.toggleNavVisibility}
              branding={() => <UviloBranding logoClass="solid" placement="center" />}
            />
          ),
        },
      }}
      {...(settings.layout === 'horizontal' && {
        horizontalLayoutProps: {
          navMenu: {
            navItems: isAuthenticated ? HorizontalNavItems() : HorizontalLandingNavItems(),

            // Uncomment the below line when using server-side menu in horizontal layout and comment the above line
            // navItems: horizontalMenuItems
          },
          appBar: {
            branding: () => <UviloBranding logoClass="solid" placement="left" />,
            nav: () =>
              navHidden ? (
                <div />
              ) : (
                <Navigation
                  settings={settings}
                  horizontalNavItems={
                    isAuthenticated ? HorizontalNavItems() : HorizontalLandingNavItems()
                  }
                />
              ),
            content: () => (
              <HorizontalAppBarContent settings={settings} saveSettings={saveSettings} />
            ),
          },
        },
      })}
    >
      {children}
    </Layout>
  );
};

export default UserLayout;
